import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Contact from "@/services/contact/contact";
import type { ApiPhoneNumberType } from "./useEditContactPhoneNumber";

export interface AddContactPhoneNumberPayload {
  contactId: string;
  organizationId?: string | null;
  phoneNumber: string;
  phoneNumberType?: ApiPhoneNumberType;
}

/**
 * Mutation composable for adding a contact phone number.
 * Uses the existing contact service endpoint and invalidates contact-related queries.
 */
export function useAddContactPhoneNumber() {
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: async (payload: AddContactPhoneNumberPayload) => {
      // The endpoint accepts a phone-number payload (not the generic EditContact DTO).
      const result = await Contact.addContactPhoneNumber(payload as any);

      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to add phone number");
      }

      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["buckets"] });
      queryClient.invalidateQueries({ queryKey: ["bucketCounts"] });
    },
  });

  return {
    addContactPhoneNumber: addMutation.mutateAsync,
    isAdding: addMutation.isPending,
    addError: addMutation.error,
  };
}
